import { type AppPreloadQuery } from '#app/__generated__/relay/AppPreloadQuery.graphql.js';
import { LazyIntercomWrapper } from '#app/components/Intercom/LazyIntercomWrapper.js';
import { GlobalModalProvider } from '#app/components/Modals/V2/GlobalModalSystem/GlobalModalProvider.js';
import { ToastProvider } from '#app/components/Toasts/components/ToastProvider.js';
import { AnalyticsProvider } from '#app/contexts/AnalyticsContext/AnalyticsProvider.js';
import { AppConfigurationProvider } from '#app/contexts/AppConfigurationContext/AppConfigurationProvider.js';
import { AuthProvider } from '#app/contexts/AuthContext/AuthProvider.js';
import { ErrorBoundaryProvider } from '#app/contexts/ErrorBoundaryContext/ErrorBoundaryProvider.js';
import { LastLocationProvider } from '#app/contexts/LastLocationContext/LastLocationProvider.js';
import { MenuProvider } from '#app/contexts/MenuContext/MenuProvider.js';
import { type PageContext } from '#app/contexts/PageContextContext/PageContextContext.js';
import { PageContextProvider } from '#app/contexts/PageContextContext/PageContextProvider.js';
import { PostHogProvider } from '#app/contexts/PostHogContext/PostHogProvider.js';
import { SidebarContextProvider } from '#app/contexts/SidebarContext/SidebarContextProvider.js';
import { WindowDimensionsProvider } from '#app/contexts/WindowDimensionsContext/WindowDimensionsProvider.js';
import { theme } from '#app/theme/theme.js';
import { type AppConfiguration } from '#app/types.js';
import { AppUIProvider } from '../contexts/AppUIContext.js';
import { GlobalStyles } from '../styles/global.styles.js';
import { AppErrorBoundary } from './AppErrorBoundary/AppErrorBoundary.js';
import { AppLoader } from './AppLoader.js';
import { AppPreload } from './AppPreload/AppPreload.js';
import { PageViewTracker } from './PageViewTracker/PageViewTracker.js';
import {
  type Environment,
  type PreloadedQuery,
  RelayEnvironmentProvider,
} from '@contra/relay';
import { OverlayProvider } from '@react-aria/overlays';
import { type AnalyticsBrowser } from '@segment/analytics-next';
import { Suspense } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from 'styled-components';

type AppProps = {
  readonly analytics: AnalyticsBrowser;
  readonly appConfiguration: AppConfiguration;
  readonly children: React.ReactNode;
  readonly pageContext: PageContext;
  readonly preloadedQuery: PreloadedQuery<AppPreloadQuery>;
  readonly relayEnvironment: InstanceType<typeof Environment>;
  readonly sentryEnabled: boolean;
};

export const AppClient = ({
  analytics,
  appConfiguration,
  children,
  pageContext,
  preloadedQuery,
  relayEnvironment,
  sentryEnabled,
}: AppProps) => {
  if (appConfiguration.playwrightTest) {
    // This has been added to ease debugging of Playwright tests.
    // eslint-disable-next-line no-console
    console.log('app configuration', appConfiguration);
  }

  return (
    <AppConfigurationProvider appConfiguration={appConfiguration}>
      <PageContextProvider pageContext={pageContext}>
        <HelmetProvider>
          <RelayEnvironmentProvider environment={relayEnvironment}>
            <ThemeProvider theme={theme}>
              <GlobalStyles />
              <OverlayProvider>
                <AppUIProvider>
                  <Suspense fallback={<AppLoader />}>
                    <AppPreload preloadedQuery={preloadedQuery}>
                      <PostHogProvider>
                        <AnalyticsProvider analytics={analytics}>
                          <LastLocationProvider>
                            <ErrorBoundaryProvider
                              sentryEnabled={sentryEnabled}
                            >
                              <AuthProvider>
                                <WindowDimensionsProvider>
                                  <ToastProvider>
                                    <GlobalModalProvider>
                                      <MenuProvider>
                                        <Helmet
                                          defaultTitle="Contra - Work the Way You Want"
                                          titleTemplate="%s | Contra"
                                        />
                                        <AppErrorBoundary>
                                          <SidebarContextProvider>
                                            <Suspense fallback={<AppLoader />}>
                                              <LazyIntercomWrapper />
                                              <PageViewTracker />
                                              {children}
                                              <div id="onboarding-tooltip-portal" />
                                              <div id="guided-tooltip-portal" />
                                            </Suspense>
                                          </SidebarContextProvider>
                                        </AppErrorBoundary>
                                      </MenuProvider>
                                    </GlobalModalProvider>
                                  </ToastProvider>
                                </WindowDimensionsProvider>
                              </AuthProvider>
                            </ErrorBoundaryProvider>
                          </LastLocationProvider>
                        </AnalyticsProvider>
                      </PostHogProvider>
                    </AppPreload>
                  </Suspense>
                </AppUIProvider>
              </OverlayProvider>
            </ThemeProvider>
          </RelayEnvironmentProvider>
        </HelmetProvider>
      </PageContextProvider>
    </AppConfigurationProvider>
  );
};
